import {
  DialogContent,
  Dialog,
  DialogProps,
  useEventCallback,
  DialogActions,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { PmiDialogTitle } from 'shell/components/shared/dialog/title';
import { PmiButton } from 'shell/components/shared/button';
import { styled } from '@mui/styles';
import { memo, useState } from 'react';
import { useFormik } from 'formik';
import {
  NewResidenceVM,
  PracticeEditResidenceCadastralFields,
} from 'practice/view-models/practice-edit-residence';
import Form from './form';
import * as yup from 'yup';
import { __PRACTICE_API_URL__ } from 'shell/constants/environment';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { PracticeAddress } from 'shell/models/practice-address';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { useUserProfile } from 'shell/hooks/use-user-profile';
import { regexOnlyNumber } from 'shell/constants/regex';
import ModalActualResidence from './modal-actual-residence';
import { HttpResponse } from 'shell/models/http-response';
import ModalWarning from 'shell/components/shared/modal-warning';
import { ObjectShape } from 'yup/lib/object';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { CadastralFieldsVisibility } from 'shell/models/practice';
import { AxiosResponse } from 'axios';
import { Message } from 'shell/components/shared/search-messages';
import { ItalianVM } from 'shell/view-models/register-office-practice-vm';
import ModalReason from 'practice/components/edit/form/shared/modal-reason';

export const PmiDialog = styled((props: DialogProps) => <Dialog {...props} />)((props) => ({
  '& .MuiDialog-paper': props.fullScreen
    ? {
        minWidth: 'unset',
        maxWidth: '100%',
      }
    : {
        minWidth: '50rem',
        maxWidth: '800px',
      },
}));

const validationSchema = (isCadastralFieldsMandatory?: boolean) => {
  let schema: ObjectShape = {};

  schema = {
    checkedResidence: yup.boolean().nullable(),
    italian: yup.object().shape({
      section: yup.string().nullable(),
      sheet: yup
        .string()
        .nullable()
        .matches(regexOnlyNumber, 'Il campo è numerico')
        .max(3, 'Massimo 3 byte'),
      particle: yup
        .string()
        .nullable()
        .matches(regexOnlyNumber, 'Il campo è numerico')
        .max(8, 'Massimo 8 byte'),
      sub: yup
        .string()
        .nullable()
        .matches(regexOnlyNumber, 'Il campo è numerico')
        .max(4, 'Massimo 4 byte'),
      cat: yup.string().nullable(),
      municipality: yup.object().nullable(),
      cap: yup.string().nullable(),
      addressAuto: yup.object().nullable(),
      number: yup.string().nullable(),
      houseOccupation: yup.string().nullable(),
      officeContract: yup.string().nullable(),
      numberContract: yup.string().matches(regexOnlyNumber, 'Il campo è numerico').nullable(),
      dateContract: yup.date().nullable().typeError('Data non valida'),
    }),
  };
  return yup
    .object({
      ...schema,
    })
    .test('municipality', 'Controllo', (values) => {
      const castedValues = values as unknown as NewResidenceVM;

      if (!castedValues.checkedResidence && !castedValues.italian?.municipality?.description) {
        return new yup.ValidationError('Campo obbligatorio', undefined, 'italian.municipality');
      }

      return true;
    })
    .test('addressAuto', 'Controllo', (values) => {
      const castedValues = values as unknown as NewResidenceVM;

      if (!castedValues.checkedResidence && !castedValues.italian?.addressAuto?.description) {
        return new yup.ValidationError('Campo obbligatorio', undefined, 'italian.addressAuto');
      }

      return true;
    })
    .test('cap', 'Controllo', (values) => {
      const castedValues = values as unknown as NewResidenceVM;

      if (!castedValues.checkedResidence && !castedValues.italian?.cap) {
        return new yup.ValidationError('Campo obbligatorio', undefined, 'italian.cap');
      }

      return true;
    })
    .test('number', 'Controllo', (values) => {
      const castedValues = values as unknown as NewResidenceVM;

      if (!castedValues.checkedResidence && !castedValues.italian?.number) {
        return new yup.ValidationError('Campo obbligatorio', undefined, 'italian.number');
      }

      return true;
    })
    .test('sheet', 'Controllo', (values) => {
      const castedValues = values as unknown as NewResidenceVM;

      if (
        castedValues.italian?.section?.toUpperCase() === 'NC' ||
        castedValues.italian?.section?.toUpperCase() === 'NP'
      ) {
        return true;
      }

      if (
        !castedValues.checkedResidence &&
        !castedValues.italian?.sheet &&
        isCadastralFieldsMandatory &&
        !castedValues?.italian?.isHomeless
      ) {
        return new yup.ValidationError('Campo obbligatorio', undefined, 'italian.sheet');
      }

      return true;
    })
    .test('particle', 'Controllo', (values) => {
      const castedValues = values as unknown as NewResidenceVM;

      if (
        castedValues.italian?.section?.toUpperCase() === 'NC' ||
        castedValues.italian?.section?.toUpperCase() === 'NP'
      ) {
        return true;
      }

      if (
        !castedValues.checkedResidence &&
        !castedValues.italian?.particle &&
        isCadastralFieldsMandatory &&
        !castedValues?.italian?.isHomeless
      ) {
        return new yup.ValidationError('Campo obbligatorio', undefined, 'italian.particle');
      }

      return true;
    })
    .test('sub', 'Controllo', (values) => {
      const castedValues = values as unknown as NewResidenceVM;

      if (
        castedValues.italian?.section?.toUpperCase() === 'NC' ||
        castedValues.italian?.section?.toUpperCase() === 'NP'
      ) {
        return true;
      }

      if (
        !castedValues.checkedResidence &&
        !castedValues.italian?.sub &&
        isCadastralFieldsMandatory &&
        !castedValues?.italian?.isHomeless
      ) {
        return new yup.ValidationError('Campo obbligatorio', undefined, 'italian.sub');
      }

      return true;
    })
    .test('foreignState', 'Controllo', (values) => {
      const castedValues = values as unknown as NewResidenceVM;

      if (castedValues.checkedResidence && !castedValues.foreign?.foreignState?.id) {
        return new yup.ValidationError('Campo obbligatorio', undefined, 'foreign.foreignState');
      }

      return true;
    })
    .test('foreignMunicipality', 'Controllo', (values) => {
      const castedValues = values as unknown as NewResidenceVM;

      if (castedValues.checkedResidence && !castedValues.foreign?.municipality?.description) {
        return new yup.ValidationError('Campo obbligatorio', undefined, 'foreign.municipality');
      }

      return true;
    })
    .test('foreignAddress', 'Controllo', (values) => {
      const castedValues = values as unknown as NewResidenceVM;

      if (castedValues.checkedResidence && !castedValues.foreign?.address) {
        return new yup.ValidationError('Campo obbligatorio', undefined, 'foreign.address');
      }

      return true;
    })
    .test('', 'Controllo Agenzia entrate', (values) => {
      const castedValues = values as unknown as NewResidenceVM;

      if (
        castedValues.italian?.houseOccupationType !== '2' &&
        castedValues.italian?.houseOccupationType !== '4'
      ) {
        return true;
      }

      const elements = ['numberContract', 'dateContract', 'officeContract'] as Array<
        keyof ItalianVM
      >;

      const hasElementPresent = elements.every((item) => {
        if (!castedValues.italian) {
          return false;
        }

        return !!castedValues.italian[item];
      });

      if (!castedValues.checkedResidence && !hasElementPresent) {
        const errors = elements
          .map((key) => {
            const value = (castedValues?.italian || {})[key];
            if (value) {
              return null;
            } // Successful validation, no error

            return new yup.ValidationError('Il campo è obbligatorio', value, `italian.${key}`);
          })
          .filter((item) => !!item) as unknown as Array<yup.ValidationError>;
        if (errors.length === 0) {
          return true;
        }
        return new yup.ValidationError(errors);
      }

      return true;
    });
};

interface ModalNewResidenceProps {
  open: boolean;
  onClose: (open: boolean) => void;
  onConfirm: (values: NewResidenceVM) => void;
  initialValues?: NewResidenceVM;
  codeEnte?: string;
  typeRegister?: string;
  intestatarioIsAnpr?: boolean;
  yearId?: string;
  progressive?: string;
  typePractice?: string;
  typeResidence?: string;
}

const ModalNewResidenceComponent = ({
  open,
  onClose,
  onConfirm,
  initialValues,
  codeEnte,
  yearId,
  typeRegister,
  progressive,
}: ModalNewResidenceProps): React.ReactElement => {
  const { userProfile } = useUserProfile();

  const [openWarning, setOpenWarning] = useState({
    open: false,
    message: '',
    values: {},
    flagMotivo: false,
  });

  const [modalReason, setOpenModalReason] = useState<{ open: boolean; warnings: Message[] }>({
    open: false,
    warnings: [],
  });

  const [openActualResidence, setOpenActualResidence] = useState<
    Record<string, boolean | NewResidenceVM>
  >({
    open: false,
    values: {},
  });

  const [indirizziControloDati] = useHttpFetch<
    HttpResponse<CadastralFieldsVisibility>,
    PracticeEditResidenceCadastralFields
  >(`${__PRACTICE_API_URL__}/pratiche`, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'GET',
      loading: true,
      data: {},
    },
    transformer: (response: AxiosResponse<HttpResponse<CadastralFieldsVisibility>>) => {
      const data = {
        isCadastralFieldsMandatory: response?.data?.payload?.datiObbligatori,
      };
      return data;
    },
  });

  const controlData = useHttpClient(
    `${__PRACTICE_API_URL__}/pratiche/registrazione-nuovo-indirizzo/controlli`,
    {
      options: { method: 'POST' },
    }
  );

  const onSubmit = useEventCallback((values: NewResidenceVM) => {
    const data = {
      pratica:
        codeEnte && yearId && typeRegister && progressive
          ? {
              key: {
                codiceEnte: codeEnte,
                tipoRegistro: typeRegister,
                idAnnoPratica: Number.parseInt(yearId, 10),
                progressivoPratica: Number.parseInt(progressive, 10),
              },
            }
          : undefined,
      indirizzo:
        values?.italian && !values?.checkedResidence
          ? {
              citta: values?.italian.municipality?.description || undefined,
              provincia: values?.italian.municipality?.province || undefined,
              cap: values?.italian?.cap || undefined,
              codVia: values?.italian?.addressAuto?.code || undefined,
              nomeVia: values?.italian?.addressAuto?.description || undefined,
              numeroCivico: values?.italian?.number || undefined,
              esponente: values?.italian?.exponent || undefined,
              // lettera: values?.italian?.letter || undefined,
              sezione: values?.italian?.section || undefined,
              foglio: values?.italian?.sheet || undefined,
              particellaMappale: values?.italian?.particle || undefined,
              subalterno: values?.italian?.sub || undefined,
              categoriaCatastale: values?.italian?.cat || undefined,
              flagIgnotaDimora: values?.italian?.isHomeless ? '1' : '0',
              //nuovi campi
              tipoOccupazione: values?.italian?.houseOccupationType || undefined,
              sedeAgenziaEntrate: values?.italian?.officeContract || undefined,
              dataRegistrazioneAgenziaEntrate:
                values?.italian?.dateContract?.toFormat('yyyy-MM-dd') || undefined,
              numeroRegistrazioneAgenziaEntrate: values?.italian?.numberContract || undefined,
            }
          : undefined,
      indirizzoEstero:
        values?.foreign && values?.checkedResidence
          ? {
              statoEstero: values?.foreign?.foreignState?.province || undefined,
              comune: values?.foreign?.municipality?.description || undefined,
              codComune: values?.foreign?.municipality?.id
                ? Number.parseInt(values?.foreign?.municipality?.id, 10)
                : undefined,
              contea: values?.foreign?.municipality?.province || undefined,
              cap: values?.foreign?.cap || undefined,
              indirizzo: values?.foreign?.address || undefined,
              civico: values?.foreign?.civicNumber || undefined,
              codiceConsolato: values?.foreign?.consulateCode?.id || undefined,
              codiceTerritorio:
                values?.foreign?.territory?.id && values?.foreign?.territory?.value
                  ? values?.foreign?.territory?.id
                  : undefined,
            }
          : undefined,
    };

    (controlData({ data }) as ResponsePromise<HttpResponse<PracticeAddress>>).then((response) => {
      const castedResponse = response?.data?.payload as PracticeAddress;

      if (castedResponse?.flagAttualeResidenza) {
        // true se la nuova residenza coincide con quella vecchia, segnalare problema
        setOpenActualResidence({
          open: true,
          values,
        });

        return;
      }

      if ((castedResponse?.warning || []).length > 0) {
        setOpenWarning({
          open: true,
          message: castedResponse?.warning?.join(', ') || '',
          values,
          flagMotivo: castedResponse?.flagMinoreSolo || false,
        });

        return;
      }

      onConfirm(values);
    });
  });

  const formContext = useFormik<NewResidenceVM>({
    initialValues: {
      checkedResidence:
        initialValues?.checkedResidence !== undefined ? initialValues.checkedResidence : true,
      flagAttualeResidenza: initialValues?.flagAttualeResidenza || false,
      italian: {
        section: undefined,
        sheet: '',
        particle: '',
        sub: '',
        cat: undefined,
        cap: undefined,
        addressAuto: undefined,
        number: undefined,
        ...(initialValues?.italian || {}),
        municipality: initialValues?.italian?.municipality || {
          description: userProfile.datiLogin?.nomeComune || '',
          province: userProfile.datiLogin?.siglaProvincia || '',
          id: userProfile.datiLogin?.codiceIstat || '',
        },
      },
      foreign: initialValues?.foreign || undefined,
    },
    validationSchema: validationSchema(
      (indirizziControloDati?.data as PracticeEditResidenceCadastralFields)
        ?.isCadastralFieldsMandatory
    ),
    onSubmit,
    enableReinitialize: true,
    validateOnMount: true,
  });

  const onChange = useEventCallback(
    (key: keyof NewResidenceVM, value: NewResidenceVM[keyof NewResidenceVM]): void => {
      formContext.setFieldValue(key, value);
    }
  );

  const onCloseWarning = useEventCallback(() => {
    const values: NewResidenceVM = openWarning.values as NewResidenceVM;

    if (openWarning.flagMotivo) {
      setOpenModalReason({
        open: true,
        warnings: [{ id: 1, value: openWarning.message }],
      });
      setOpenWarning({ open: false, message: '', values: {}, flagMotivo: false });

      return;
    }

    setOpenWarning({ open: false, message: '', values: {}, flagMotivo: false });

    onConfirm(values);
  });

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.md));

  return (
    <>
      <PmiDialog
        fullScreen={fullScreen}
        open={open}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <PmiDialogTitle id="alert-dialog-title" backgroundColor="primary" color="white">
          Residenza
        </PmiDialogTitle>
        <DialogContent style={{ padding: '30px 24px' }}>
          <Form
            onChange={onChange}
            values={formContext.values}
            errors={formContext.errors}
            touched={formContext.touched}
          />
        </DialogContent>
        <DialogActions>
          <PmiButton
            variant="contained"
            color="primary"
            onClick={() => formContext.handleSubmit()}
            disabled={!formContext.isValid}
          >
            Conferma
          </PmiButton>
          <PmiButton variant="outlined" color="primary" type="button" onClick={() => onClose(true)}>
            Chiudi
          </PmiButton>
        </DialogActions>
      </PmiDialog>
      <ModalActualResidence
        open={openActualResidence.open as boolean}
        onCancel={() => {
          setOpenActualResidence({
            open: false,
            values: {},
          });
        }}
        onOkClose={() => {
          onConfirm({
            ...(openActualResidence.values as NewResidenceVM),
            flagAttualeResidenza: true,
          });

          setOpenActualResidence({
            open: false,
            values: {},
          });
        }}
      />
      <ModalWarning
        open={openWarning.open}
        onClose={onCloseWarning}
        message={openWarning.message}
        withAnnulment
        onAnnulment={() =>
          setOpenWarning({ open: false, message: '', values: {}, flagMotivo: false })
        }
        nameButton="Prosegui"
      />
      {modalReason.open ? (
        <ModalReason
          open={modalReason.open}
          onClose={() => {
            setOpenWarning({
              open: true,
              message: modalReason.warnings[0].value,
              values: formContext.values,
              flagMotivo: true,
            });
            setOpenModalReason({ open: false, warnings: [] });
          }}
          onConfirm={() => {
            setOpenModalReason({ open: false, warnings: [] });
            onConfirm(formContext.values);
          }}
          warnings={modalReason.warnings}
        />
      ) : undefined}
    </>
  );
};

ModalNewResidenceComponent.displayName = 'Modal new residence';

const ModalNewResidence = memo(ModalNewResidenceComponent);

export default ModalNewResidence;
